import React, { useState } from "react";
import {
  Box,
  Button,
  Center,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FaFileExcel } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import { exportSearchProfiles } from "../../api/searchApi";
import { batchColorScheme } from "./colors";
import LoadMoreButton from "./LoadMoreButton";
import ProfileCard from "./ProfileCard";

const CandidatesModal = ({
  isOpen,
  onClose,
  search,
  buId,
  profiles = [],
  profilesMeta,
  isLoadingProfiles,
  onProfileUpdated,
  onLoadMoreAppended,
}) => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!search?.id || isExporting) return;
    setIsExporting(true);
    try {
      const blob = await exportSearchProfiles(search.id, { buId });
      const filename = `search_${search.id}_profiles.xlsx`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export search profiles failed", err);
      const detail = err?.response?.data?.detail;
      const description =
        (typeof detail === "string" ? detail : detail?.code) ||
        err?.message ||
        undefined;
      toast({
        title: t("asapProfile.export.errorTitle"),
        description,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (!search) return null;
  const status = search.status;
  const canExport = status === "completed" && profiles.length > 0;

  // Distinct loading batches present, in load order. The colour legend is
  // only worth showing once a second batch has been loaded — with a single
  // batch there is nothing to tell apart.
  const batchesPresent = [
    ...new Set(profiles.map((p) => p.batchIndex || 1)),
  ].sort((a, b) => a - b);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent data-testid="candidates-modal">
        <ModalHeader as="div" pr="56px" sx={{ marginBottom: "0 !important" }}>
          <HStack justify="space-between" align="center">
            <Heading
              as="h2"
              size="md"
              sx={{ marginBottom: "0 !important" }}
            >
              {t("asapProfile.profiles.modalTitle")}
            </Heading>
            {canExport && (
              <Button
                size="sm"
                leftIcon={<FaFileExcel />}
                colorScheme="teal"
                variant="outline"
                onClick={handleExport}
                isLoading={isExporting}
                data-testid="candidates-modal-export"
              >
                {t("asapProfile.export.button")}
              </Button>
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoadingProfiles ? (
            <Center py={6}>
              <Spinner />
            </Center>
          ) : status === "completed" && profiles.length === 0 ? (
            <Center py={6} color="gray.500" data-testid="profiles-empty-state">
              <Text sx={{ marginBottom: "0 !important" }}>
                {t("asapProfile.profiles.emptyV2", {
                  defaultValue:
                    "We searched the locations and skills you selected and could not find profiles matching these criteria. Try widening the skills or location.",
                })}
              </Text>
            </Center>
          ) : profiles.length > 0 ? (
            <VStack
              align="stretch"
              spacing={3}
              data-testid="profile-card-list"
            >
              {batchesPresent.length > 1 && (
                <Wrap
                  spacing={4}
                  align="center"
                  pb={1}
                  data-testid="batch-legend"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.600"
                    sx={{ marginBottom: "0 !important" }}
                  >
                    {t("asapProfile.batch.legendTitle")}
                  </Text>
                  {batchesPresent.map((b) => (
                    <WrapItem key={b}>
                      <HStack spacing={2} align="center">
                        <Box
                          w="12px"
                          h="12px"
                          borderRadius="sm"
                          bg={`${batchColorScheme(b)}.400`}
                          data-testid={`batch-legend-swatch-${b}`}
                        />
                        <Text
                          fontSize="sm"
                          color="gray.600"
                          sx={{ marginBottom: "0 !important" }}
                        >
                          {t("asapProfile.batch.item", { number: b })}
                        </Text>
                      </HStack>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
              {profiles.map((p) => (
                <ProfileCard
                  key={p.id}
                  profile={p}
                  searchId={search.id}
                  buId={buId}
                  onUpdated={onProfileUpdated}
                />
              ))}
            </VStack>
          ) : (
            <Center py={6} color="gray.500">
              <Text sx={{ marginBottom: "0 !important" }}>
                {t("asapProfile.profiles.notRunYet")}
              </Text>
            </Center>
          )}
        </ModalBody>
        {status === "completed" && profiles.length > 0 && (
          <ModalFooter>
            <Box w="full">
              <LoadMoreButton
                searchId={search.id}
                buId={buId}
                loadedCount={profilesMeta.pdlOffsetLoaded || profiles.length}
                loadMoreCount={profilesMeta.loadMoreCount ?? 0}
                maxLoadMorePerSearch={profilesMeta.maxLoadMorePerSearch ?? null}
                exhausted={profilesMeta.exhausted ?? false}
                onAppended={onLoadMoreAppended}
              />
            </Box>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default CandidatesModal;
